"use client";

import { useState, useEffect } from "react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apiFetch } from "../../lib/api";
import {
  IconClipboard, IconSwords, IconBarChart, IconFolder, IconUpload, IconPlus,
  IconLink, IconBolt, IconX, IconUndo, IconSkipForward, IconCheck,
} from "../../components/icons";
import PlayerAvatar from "../../components/PlayerAvatar";

const posColors: Record<string, string> = {
  QB: "text-[#f38ba8]", RB: "text-[#a6e3a1]", WR: "text-[#89b4fa]",
  TE: "text-[#fab387]", K: "text-[#cba6f7]", ROOKIE: "text-[#cba6f7]",
};

const filterPositions = ["All", "QB", "RB", "WR", "TE", "K", "DEF", "ROOKIE"];

type UploadDuplicateRow = {
  row_id: number;
  uploaded_name: string;
  rank: string | null;
  team: string | null;
  pos: string | null;
};

type UploadConflictRow = {
  row_id: number;
  name: string;
  csv_team: string | null;
  csv_pos: string | null;
  sleeper_team: string | null;
  sleeper_pos: string | null;
};

type UploadReview = {
  unrecognized: string[];
  duplicates: { name: string; rows: UploadDuplicateRow[] }[];
  conflicts: UploadConflictRow[];
};

function SortablePlayerRow({
  player, showTierHeader, onSplitTier, onMergeTier, compareLists, compareData, tierEditsDisabled
}: {
  player: any; showTierHeader: boolean; onSplitTier: () => void; onMergeTier: () => void;
  compareLists: string[]; compareData: Record<string, Record<string, number>>; tierEditsDisabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: player.Name });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const posColorClass = posColors[player.Pos] || "text-blue-300";

  return (
    <li ref={setNodeRef} style={style} className="list-none w-full">
      {showTierHeader && (
        <div className="py-2 mt-4 mb-2 text-[#f9e2af] font-bold border-b border-[#f9e2af]/30 text-sm tracking-widest flex items-center justify-between">
          <span>--- TIER {player.Tier} ---</span>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onMergeTier}
            disabled={tierEditsDisabled}
            title={tierEditsDisabled ? "Switch to All to restructure tiers" : undefined}
            className="text-zinc-400 hover:text-amber-400 text-xs flex items-center gap-1 transition-colors disabled:opacity-30 disabled:hover:text-zinc-400 disabled:cursor-not-allowed"
          >
            <IconLink className="w-3.5 h-3.5" /> Merge Below
          </button>
        </div>
      )}

      {/* UPDATED: Proper Table Row Layout (items-stretch ensures borders are full height) */}
      <div {...attributes} {...listeners} className="bg-zinc-700 rounded flex items-stretch cursor-grab active:cursor-grabbing hover:bg-zinc-600 border border-zinc-600 shadow-sm overflow-hidden">

        {/* Photo — flush against the row's left edge, stretched to the row's full height */}
        {player.Pos && (
          <PlayerAvatar
            name={player.Name}
            colorClass={posColorClass}
            photoUrl={player.PhotoUrl}
            className="w-14 sm:w-16 self-stretch shrink-0"
          />
        )}

        {/* Column 1: Player Info (Flexible Width, guaranteed a usable minimum so it never collapses on narrow screens) */}
        <div className="flex-1 min-w-0 p-2 sm:p-3 flex items-center gap-2 sm:gap-3">
          <span className="text-zinc-200 font-medium truncate text-sm sm:text-base">
            {player.Pos && <strong className={posColorClass}>[{player.Pos}] </strong>}{player.Name}
            {player.Team && <span className="text-zinc-400 ml-2 text-xs sm:text-sm">({player.Team})</span>}
          </span>
        </div>

        {/* Column 2: Base Master Ranks (narrower + abbreviated on mobile) */}
        <div className="w-[92px] sm:w-[200px] shrink-0 p-2 sm:p-3 flex items-center justify-end border-l border-zinc-600/50 bg-zinc-800/30">
          <span className="text-zinc-400 font-mono text-[11px] sm:text-sm text-right">
            <span className="hidden sm:inline">Tier {player.Tier} | Rank {player.Rank} | </span>
            <span className="sm:hidden">T{player.Tier} R{player.Rank} </span>
            <span className="text-amber-200">{player.PosRank}</span>
          </span>
        </div>

        {/* Columns 3+: Comparison Lists (hidden on mobile — power-user desktop feature, no room to show it meaningfully) */}
        {compareLists.map(lName => {
          const compRank = compareData[lName]?.[player.Name];
          let diffColor = "text-zinc-500";

          if (compRank) {
             const diff = compRank - player.Rank;
             if (diff >= 3) diffColor = "text-green-400";
             else if (diff <= -3) diffColor = "text-red-400";
             else diffColor = "text-blue-300";
          }

          return (
            <div key={lName} className={`hidden sm:flex w-[80px] shrink-0 p-3 flex-col items-center justify-center border-l border-zinc-600/50 bg-zinc-900/50 font-mono text-sm font-bold ${diffColor}`} title={`Ranked #${compRank || 'N/A'} in ${lName}`}>
               {compRank || '-'}
            </div>
          );
        })}

        {/* Final Column: Action Button (Fixed Width) */}
        <div className="w-[36px] sm:w-[50px] shrink-0 flex items-center justify-center border-l border-zinc-600/50 bg-zinc-800/30">
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onSplitTier}
            disabled={tierEditsDisabled}
            className="text-amber-400/50 hover:text-amber-400 transition-colors disabled:opacity-20 disabled:hover:text-amber-400/50 disabled:cursor-not-allowed"
            title={tierEditsDisabled ? "Switch to All to restructure tiers" : "Split Tier Here"}
          >
            <IconBolt className="w-4 h-4" />
          </button>
        </div>

      </div>
    </li>
  );
}

export default function RankingsPage() {
  const [players, setPlayers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [posFilter, setPosFilter] = useState("All");
  const [currentListName, setCurrentListName] = useState("My Rankings");
  
  const [showGenerator, setShowGenerator] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [showCompare, setShowCompare] = useState(false); 
  const [availableLists, setAvailableLists] = useState<string[]>([]);
  
  const [compareLists, setCompareLists] = useState<string[]>([]);
  const [compareData, setCompareData] = useState<Record<string, Record<string, number>>>({});

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadListName, setUploadListName] = useState("");
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadReview, setUploadReview] = useState<UploadReview | null>(null);
  const [unrecognizedDecisions, setUnrecognizedDecisions] = useState<Record<string, "include" | "exclude">>({});
  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<string, { action: "keep_one" | "keep_all"; row_id?: number }>>({});
  const [conflictDecisions, setConflictDecisions] = useState<Record<number, "csv" | "sleeper">>({});

  const [importDefaultListName, setImportDefaultListName] = useState("");
  const [isImportingDefault, setIsImportingDefault] = useState(false);

  const [showH2H, setShowH2H] = useState(false);
  const [h2hMatchup, setH2hMatchup] = useState<{pA: any, pB: any} | null>(null);
  // Shared undo history for every mutating action (drag, split, merge, H2H
  // choices) — not just H2H, so a filtered-view drag mistake is one click
  // to revert too. pA/pB are only set for H2H entries, used to restore the
  // matchup on undo.
  const [editHistory, setEditHistory] = useState<{ playersState: any[]; pA?: any; pB?: any }[]>([]);
  const [maxTierDiff, setMaxTierDiff] = useState(1);
  const [maxRankDiff, setMaxRankDiff] = useState(15);

  const [formConfig, setFormConfig] = useState({
    list_name: "12 Team Superflex Rankings", base_source: "My Rankings", format: "Dynasty",
    qb_type: "Superflex", te_premium: 0.5, pass_td: 4, teams: 12
  });

  const [lastMoveMsg, setLastMoveMsg] = useState("");
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const [loaderPanelError, setLoaderPanelError] = useState("");

  useEffect(() => {
    const storedList = localStorage.getItem("dynasty_active_rankings");
    // No hardcoded "My Rankings" guess — a stored preference is only used if
    // it's genuinely something the user picked before, never assumed.
    const savedList = storedList && storedList !== "Master Rankings" ? storedList : null;

    const init = async () => {
      // Find out what this account actually has *before* deciding what to
      // load, so the choice is always based on real data — never a guessed
      // name that may not exist for this account at all.
      let existingLists: string[] = [];
      try {
        const listsRes = await apiFetch(`/rankings/lists`);
        const listsData = await listsRes.json();
        existingLists = listsRes.ok ? (listsData.lists || []) : [];
      } catch {
        existingLists = [];
      }
      setAvailableLists(existingLists);

      if (existingLists.length === 0) {
        // No ranking list exists for this account at all (e.g. its only
        // list was somehow lost). Nothing to load — guide the user to
        // create one instead of showing a dead-end error.
        setPlayers([]);
        setCurrentListName("");
        setShowUploader(true);
        setRecoveryNotice("You don't have a ranking list yet. Import a default list or upload a CSV below to get started.");
        setLoading(false);
        return;
      }

      // Use the stored preference only if it's still real; otherwise just
      // take whichever list this account actually has. Only show a notice
      // when a stored preference existed and is now gone — not when there
      // was simply nothing stored yet (e.g. first visit, or the account's
      // only list was never named "My Rankings" to begin with).
      const hadStalePreference = !!savedList && !existingLists.includes(savedList);
      const targetList = savedList && existingLists.includes(savedList) ? savedList : existingLists[0];
      setCurrentListName(targetList);
      localStorage.setItem("dynasty_active_rankings", targetList);
      if (hadStalePreference) {
        setRecoveryNotice(`Couldn't find your last-used list "${savedList}" — switched you to "${targetList}". Use Load to pick a different one.`);
      }

      try {
        const res = await apiFetch(`/rankings/load/${encodeURIComponent(targetList)}`);
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.detail || `Unable to load the list "${targetList}".`);
        }
        const data = await res.json();
        setPlayers(data.data);
      } catch (err) {
        console.error("API Error:", err);
        setLoadError(err instanceof Error ? err.message : "Unable to load your rankings.");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  function saveToBackend(payload: any[]) {
    apiFetch(`/rankings/reorder`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ list_name: currentListName, players: payload })
    })
      .then(res => {
        if (!res.ok) throw new Error("Save failed");
        setSaveError("");
      })
      .catch(err => {
        console.error("Failed to save data", err);
        setSaveError("Couldn't save your last change — check your connection and try again.");
      });
  }

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    // The drag happens inside a SortableContext built from the FILTERED
    // (displayedPlayers) list, but reordering must operate on the true full
    // list — two players that look adjacent while filtered to one position
    // usually aren't adjacent in overall rank. Splicing the dragged player
    // out and reinserting them next to the real drop target (both resolved
    // by Name, never by raw index) means nobody else's relative order ever
    // shifts, and Tier/Rank stay consistent by construction — same approach
    // the H2H matchup mover already uses (movePlayerToTier below).
    const oldIndex = players.findIndex((item) => item.Name === active.id);
    const overIndexOriginal = players.findIndex((item) => item.Name === over.id);
    if (oldIndex === -1 || overIndexOriginal === -1) return;

    setEditHistory(prev => [...prev, { playersState: [...players] }]);

    const targetTier = players[overIndexOriginal].Tier;
    const draggingDown = oldIndex < overIndexOriginal;

    const rest = [...players];
    const [moved] = rest.splice(oldIndex, 1);
    const movedRec = { ...moved, Tier: targetTier };

    const overIndexNow = rest.findIndex((item) => item.Name === over.id);
    const insertIndex = draggingDown ? overIndexNow + 1 : overIndexNow;
    rest.splice(insertIndex, 0, movedRec);

    const withRanks = rest.map((p, idx) => ({ ...p, Rank: idx + 1 }));
    saveToBackend(withRanks.map(p => ({ name: p.Name, rank: p.Rank, tier: p.Tier })));
    setPlayers(withRanks);
  }

  function handleSplitTier(targetName: string) {
    setEditHistory(prev => [...prev, { playersState: [...players] }]);
    setPlayers((currentPlayers) => {
      const targetIndex = currentPlayers.findIndex(p => p.Name === targetName);
      if (targetIndex === -1) return currentPlayers;
      const updatedPlayers = currentPlayers.map((p, idx) => idx >= targetIndex ? { ...p, Tier: p.Tier + 1 } : p);
      saveToBackend(updatedPlayers.map(p => ({ name: p.Name, rank: p.Rank, tier: p.Tier })));
      return updatedPlayers;
    });
  }

  function handleMergeTierBelow(targetTier: number) {
    setEditHistory(prev => [...prev, { playersState: [...players] }]);
    setPlayers((currentPlayers) => {
      if (!currentPlayers.some(p => p.Tier > targetTier)) return currentPlayers;
      const updatedPlayers = currentPlayers.map(p => p.Tier > targetTier ? { ...p, Tier: p.Tier - 1 } : p);
      saveToBackend(updatedPlayers.map(p => ({ name: p.Name, rank: p.Rank, tier: p.Tier })));
      return updatedPlayers;
    });
  }

  const closeAllPanels = () => {
    setShowGenerator(false); setShowLoader(false); setShowUploader(false); setShowCompare(false);
  };

  const handleLoadList = async (name: string) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/rankings/load/${encodeURIComponent(name)}`);
      const result = await res.json();
      if (!res.ok) {
        // A direct, user-initiated action failing (they clicked this exact
        // list in the panel) — keep whatever's already loaded on screen and
        // name the specific list that's gone, rather than blocking the whole
        // page. Also refresh the panel so a stale/deleted entry disappears.
        setLoaderPanelError(
          typeof result.detail === "string" ? result.detail : `Unable to load "${name}".`
        );
        apiFetch(`/rankings/lists`).then(r => r.json()).then(d => setAvailableLists(d.lists));
        return;
      }
      setPlayers(result.data);
      setCurrentListName(result.list_name);
      localStorage.setItem("dynasty_active_rankings", name);
      setCompareLists([]);
      setCompareData({});
      setLoaderPanelError("");
      closeAllPanels();
    } catch (err) { console.error("Failed to load list", err); }
    finally { setLoading(false); }
  };

  const toggleCompareList = async (listName: string) => {
    if (compareLists.includes(listName)) {
      setCompareLists(prev => prev.filter(l => l !== listName));
      const newData = { ...compareData };
      delete newData[listName];
      setCompareData(newData);
    } else {
      if (compareLists.length >= 4) {
         alert("You can only compare up to 5 lists total (1 Master + 4 Comparisons).");
         return;
      }
      try {
         const res = await apiFetch(`/rankings/load/${encodeURIComponent(listName)}`);
         const result = await res.json();
         const rankMap: Record<string, number> = {};
         result.data.forEach((p: any) => { rankMap[p.Name] = p.Rank; });
         setCompareData(prev => ({ ...prev, [listName]: rankMap }));
         setCompareLists(prev => [...prev, listName]);
      } catch (err) { console.error("Failed to load comparison list", err); }
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || !uploadListName) {
      setUploadErrors(["Please provide both a List Name and a CSV file."]);
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("list_name", uploadListName);
    if (uploadReview) {
      formData.append("confirmed_unrecognized", JSON.stringify(uploadReview.unrecognized.filter(name => unrecognizedDecisions[name] === "include")));
      formData.append("excluded_unrecognized", JSON.stringify(uploadReview.unrecognized.filter(name => unrecognizedDecisions[name] === "exclude")));
      formData.append("duplicate_decisions", JSON.stringify(duplicateDecisions));
      formData.append("conflict_decisions", JSON.stringify(conflictDecisions));
    }

    try {
      const res = await apiFetch("/rankings/upload", { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail?.review_required) {
          const review = data.detail as UploadReview;
          const nextDuplicateDecisions: Record<string, { action: "keep_one" | "keep_all"; row_id?: number }> = {};
          review.duplicates.forEach(group => {
            nextDuplicateDecisions[group.name] = { action: "keep_one", row_id: group.rows[0]?.row_id };
          });
          // Default to trusting Sleeper's live data — the user can still flip
          // any individual row back to the CSV's value before confirming.
          const nextConflictDecisions: Record<number, "csv" | "sleeper"> = {};
          review.conflicts.forEach(c => { nextConflictDecisions[c.row_id] = "sleeper"; });
          setUploadReview(review);
          setUnrecognizedDecisions({});
          setDuplicateDecisions(nextDuplicateDecisions);
          setConflictDecisions(nextConflictDecisions);
          setUploadErrors([]);
        } else {
          setUploadErrors(data.detail?.errors || ["An unknown error occurred during upload."]);
        }
      } else {
        setPlayers(data.data);
        setCurrentListName(data.list_name);
        localStorage.setItem("dynasty_active_rankings", data.list_name);
        closeAllPanels();
        setUploadFile(null);
        setUploadListName("");
        setUploadReview(null);
        setUnrecognizedDecisions({});
        setDuplicateDecisions({});
        setConflictDecisions({});
        setRecoveryNotice("");
        setLoadError("");
        apiFetch(`/rankings/lists`).then(r => r.json()).then(d => setAvailableLists(d.lists));
      }
    } catch (err) { setUploadErrors(["Network error. Ensure the Python backend is running."]); }
    finally { setIsUploading(false); }
  };

  const handleImportDefault = async (source: "DynastySF_Default" | "Redraft_Default") => {
    if (!importDefaultListName) {
      setUploadErrors(["Please provide a List Name for the imported default."]);
      return;
    }
    setIsImportingDefault(true);
    setUploadErrors([]);
    try {
      const res = await apiFetch("/rankings/import-default", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ list_name: importDefaultListName, source }),
      });
      const data = await res.json();
      if (!res.ok) {
        setUploadErrors([typeof data.detail === "string" ? data.detail : "Unable to import that default list."]);
        return;
      }
      setPlayers(data.data);
      setCurrentListName(data.list_name);
      localStorage.setItem("dynasty_active_rankings", data.list_name);
      closeAllPanels();
      setImportDefaultListName("");
      setRecoveryNotice("");
      setLoadError("");
      apiFetch(`/rankings/lists`).then(r => r.json()).then(d => setAvailableLists(d.lists));
    } catch (err) {
      setUploadErrors(["Network error. Ensure the Python backend is running."]);
    } finally {
      setIsImportingDefault(false);
    }
  };

  const uploadReviewReady = !uploadReview || (
    uploadReview.unrecognized.every(name => !!unrecognizedDecisions[name]) &&
    uploadReview.duplicates.every(group => duplicateDecisions[group.name]) &&
    uploadReview.conflicts.every(c => !!conflictDecisions[c.row_id])
  );

  const setAllUnrecognized = (decision: "include" | "exclude") => {
    if (!uploadReview) return;
    setUnrecognizedDecisions(Object.fromEntries(uploadReview.unrecognized.map(name => [name, decision])));
  };

  const setAllConflicts = (decision: "csv" | "sleeper") => {
    if (!uploadReview) return;
    setConflictDecisions(Object.fromEntries(uploadReview.conflicts.map(c => [c.row_id, decision])));
  };

  const keepFirstDuplicateRows = () => {
    if (!uploadReview) return;
    setDuplicateDecisions(Object.fromEntries(uploadReview.duplicates.map(group => [
      group.name,
      { action: "keep_one" as const, row_id: group.rows[0]?.row_id }
    ])));
  };

  const toggleH2H = () => {
    const newState = !showH2H;
    setShowH2H(newState);
    if (newState && !h2hMatchup) generateMatchup(players);
  };

  const generateMatchup = (currentPlayers: any[]) => {
    if (currentPlayers.length < 2) return;
    let weightPool: number[] = [];
    currentPlayers.forEach((p, index) => {
        let w = 1.0;
        if (p.Rank <= 240) w = 4.0;
        if (p.Tier > 12) w = 0.2;
        let times = Math.floor(w * 10);
        for (let i = 0; i < times; i++) weightPool.push(index);
    });
    let idxA = weightPool[Math.floor(Math.random() * weightPool.length)];
    let playerA = currentPlayers[idxA];
    let validB = currentPlayers.filter(p => p.Name !== playerA.Name && Math.abs(p.Tier - playerA.Tier) <= maxTierDiff && Math.abs(p.Rank - playerA.Rank) <= maxRankDiff);
    if (validB.length === 0) validB = currentPlayers.filter(p => p.Name !== playerA.Name && Math.abs(p.Tier - playerA.Tier) <= maxTierDiff);
    if (validB.length === 0) validB = currentPlayers.filter(p => p.Name !== playerA.Name);
    let playerB = validB[Math.floor(Math.random() * validB.length)];
    setH2hMatchup({ pA: playerA, pB: playerB });
  };

  const handleH2HChoice = (choice: string) => {
    if (!h2hMatchup) return;
    setEditHistory([...editHistory, { playersState: [...players], pA: h2hMatchup.pA, pB: h2hMatchup.pB }]);
    
    let newPlayers = [...players];
    let pA = h2hMatchup.pA;
    let pB = h2hMatchup.pB;
    let msg = "";

    const getTopOfTier = (recs: any[], t: number) => {
      const index = recs.findIndex(r => r.Tier >= t);
      return index === -1 ? recs.length : index;
    };
    const getBottomOfTier = (recs: any[], t: number) => {
      let insert = getTopOfTier(recs, t);
      for (let i = insert; i < recs.length; i++) {
        if (recs[i].Tier !== t) break;
        insert = i + 1;
      }
      return insert;
    };
    const movePlayerToTier = (name: string, tier: number, edge: "top" | "bottom") => {
      const idx = newPlayers.findIndex(r => r.Name === name);
      if (idx === -1) return null;

      const rec = { ...newPlayers.splice(idx, 1)[0], Tier: tier };
      const insertIndex = edge === "top" ? getTopOfTier(newPlayers, tier) : getBottomOfTier(newPlayers, tier);
      newPlayers.splice(insertIndex, 0, rec);
      return rec;
    };

    const processMove = (winner: any, loser: any, type: string) => {
      // Safely extract names whether passed as objects or strings
      const winnerName = typeof winner === 'object' ? winner.Name : winner;
      const loserName = typeof loser === 'object' ? loser.Name : loser;

      if (type.startsWith('down')) {
        const requestedNames = (type === 'down_a') ? [winnerName] : (type === 'down_b') ? [loserName] : [winnerName, loserName];
        const toMove = [...requestedNames].sort((a, b) => newPlayers.findIndex(r => r.Name === b) - newPlayers.findIndex(r => r.Name === a));
        const destinationTiers: Record<string, number> = {};
        
        for (const name of toMove) {
          const idx = newPlayers.findIndex(r => r.Name === name);
          if (idx === -1) continue;
          const targetTier = newPlayers[idx].Tier + 1;
          const moved = movePlayerToTier(name, targetTier, "top");
          if (moved) destinationTiers[name] = targetTier;
        }

        if (requestedNames.length === 1) {
          const [name] = requestedNames;
          return `${name} moved down to top of Tier ${destinationTiers[name]}.`;
        }

        return `Both ${requestedNames[0]} and ${requestedNames[1]} moved down to top of Tier ${destinationTiers[requestedNames[0]]} and Tier ${destinationTiers[requestedNames[1]]}, respectively.`;
      }

      let wIdx = newPlayers.findIndex(r => r.Name === winnerName);
      let lIdx = newPlayers.findIndex(r => r.Name === loserName);
      
      // Safety check
      if (wIdx === -1 || lIdx === -1) {
        console.error("Player not found in array by name:", winnerName, loserName);
        return "Error: Player not found in rankings.";
      }

      let wRec = newPlayers[wIdx];
      let lRec = newPlayers[lIdx];
      let tw = wRec.Tier;
      let tl = lRec.Tier;

      if (tw === tl) {
        if (type === 'always') {
           if (tw === 1) {
              movePlayerToTier(loserName, lRec.Tier + 1, "top");
              return `${winnerName} dominates Tier 1; ${loserName} bumped to Tier 2.`;
           } else {
              const moved = movePlayerToTier(winnerName, wRec.Tier - 1, "bottom");
              return `${winnerName} moved up to bottom of Tier ${moved?.Tier}.`;
           }
        } else {
           if (wIdx > lIdx) { newPlayers.splice(wIdx, 1); newPlayers.splice(lIdx, 0, wRec); return `${winnerName} moved just above ${loserName} in Tier ${wRec.Tier}.`; }
           return `${winnerName} is already ranked above ${loserName}. No change.`;
        }
      } else if (tw < tl) {
        if (type !== 'always') {
           movePlayerToTier(loserName, tw, "bottom");
           return `${loserName} moved up to bottom of Tier ${tw}.`;
        }
        return `${winnerName} is already ranked a tier above ${loserName}. No change.`;
      } else {
        if (type === 'always') {
           const targetTier = Math.max(1, lRec.Tier - 1);
           movePlayerToTier(winnerName, targetTier, "bottom");
           return `${winnerName} moved to bottom of Tier ${targetTier}.`;
        } else {
           const moved = { ...newPlayers.splice(wIdx, 1)[0], Tier: tl };
           const loserIndex = newPlayers.findIndex(r => r.Name === loserName);
           newPlayers.splice(loserIndex, 0, moved);
           return `${winnerName} moved into Tier ${tl} just above ${loserName}.`;
        }
      }
    };

    if (choice === '1') msg = processMove(pA, pB, 'always');
    else if (choice === '2') msg = processMove(pA, pB, 'same');
    else if (choice === '3') msg = processMove(pB, pA, 'same');
    else if (choice === '4') msg = processMove(pB, pA, 'always');
    else if (choice === '5') msg = processMove(pA, pB, 'down_a');
    else if (choice === '6') msg = processMove(pA, pB, 'down_both');
    else if (choice === '7') msg = processMove(pA, pB, 'down_b');
    else msg = "Matchup skipped.";

    if (choice !== 'skip') {
      newPlayers = newPlayers.map((r, i) => ({ ...r, Rank: i + 1 }));
      setPlayers(newPlayers);
      saveToBackend(newPlayers.map(p => ({ name: p.Name, rank: p.Rank, tier: p.Tier })));
    }
    
    setLastMoveMsg(msg); // Display the status
    generateMatchup(choice !== 'skip' ? newPlayers : players);
  };

  const handleUndo = () => {
    if (editHistory.length === 0) return;
    const last = editHistory[editHistory.length - 1];
    setEditHistory(editHistory.slice(0, -1));
    setPlayers(last.playersState);
    if (last.pA && last.pB) setH2hMatchup({ pA: last.pA, pB: last.pB });
    saveToBackend(last.playersState.map((p: any) => ({ name: p.Name, rank: p.Rank, tier: p.Tier })));
  };

  const handleGenerateRanks = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/rankings/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formConfig)
      });
      const result = await res.json();
      if (!res.ok) {
        alert(typeof result.detail === "string" ? result.detail : "Unable to create this ranking list.");
        return;
      }
      setPlayers(result.data); setCurrentListName(result.list_name); localStorage.setItem("dynasty_active_rankings", result.list_name); closeAllPanels();
      apiFetch(`/rankings/lists`).then(r => r.json()).then(d => setAvailableLists(d.lists));
    } catch (err) { console.error("Failed to generate ranks", err); } 
    finally { setLoading(false); }
  };

  if (loadError) {
    return (
      <main className="min-h-screen bg-zinc-900 text-zinc-200 p-3 sm:p-10 font-sans flex items-center justify-center">
        <div className="max-w-md w-full bg-zinc-800 rounded-lg p-8 shadow-2xl border border-red-500/40 text-center">
          <h1 className="text-xl font-bold text-red-400 mb-2">Couldn&apos;t load your rankings</h1>
          <p className="text-zinc-400">{loadError}</p>
        </div>
      </main>
    );
  }

  const posCounters: Record<string, number> = {};
  const playersWithPosRanks = players.map(p => {
    posCounters[p.Pos] = (posCounters[p.Pos] || 0) + 1;
    return { ...p, PosRank: `${p.Pos}${posCounters[p.Pos]}` };
  });

  const displayedPlayers = playersWithPosRanks.filter(p => posFilter === "All" || p.Pos === posFilter);

  return (
    <main className="min-h-screen bg-zinc-900 text-zinc-200 p-3 sm:p-10 font-sans">
      <div className="max-w-5xl mx-auto">
        {recoveryNotice && (
          <div className="mb-6 flex items-start justify-between gap-3 bg-amber-950/30 border border-amber-500/40 rounded-lg p-4">
            <p className="text-sm text-amber-200">{recoveryNotice}</p>
            <button onClick={() => setRecoveryNotice("")} className="text-amber-400 hover:text-amber-200 text-lg leading-none" aria-label="Dismiss">×</button>
          </div>
        )}
        {saveError && (
          <div className="mb-6 flex items-start justify-between gap-3 bg-red-950/30 border border-red-500/40 rounded-lg p-4">
            <p className="text-sm text-red-200">{saveError}</p>
            <button onClick={() => setSaveError("")} className="text-red-400 hover:text-red-200 text-lg leading-none" aria-label="Dismiss">×</button>
          </div>
        )}
        <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-bold text-orange-400"><IconClipboard className="w-6 h-6 sm:w-7 sm:h-7 shrink-0" /> {currentListName || "Get Started"}</h1>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={toggleH2H} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded font-bold shadow-md transition-colors ${showH2H ? "bg-amber-600 hover:bg-amber-500 text-white" : "bg-zinc-700 hover:bg-zinc-600 text-amber-400 border border-zinc-600"}`}>
              {showH2H ? "Close H2H" : <><IconSwords className="w-4 h-4" /> Head-to-Head Compare</>}
            </button>
            <button onClick={() => { const s = !showCompare; closeAllPanels(); setShowCompare(s); }} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded font-bold border border-zinc-600 transition-colors ${showCompare || compareLists.length > 0 ? "bg-amber-600 text-white" : "bg-zinc-700 hover:bg-zinc-600"}`}><IconBarChart className="w-4 h-4" /> Compare</button>
            <button onClick={() => { const s = !showLoader; closeAllPanels(); setShowLoader(s); setLoaderPanelError(""); }} className="inline-flex items-center gap-1.5 bg-zinc-700 hover:bg-zinc-600 px-4 py-2 rounded font-bold border border-zinc-600"><IconFolder className="w-4 h-4" /> Load</button>
            <button onClick={() => { const s = !showUploader; closeAllPanels(); setShowUploader(s); }} className="inline-flex items-center gap-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 px-4 py-2 rounded font-bold shadow-md border border-zinc-600 transition-colors"><IconUpload className="w-4 h-4" /> Upload</button>
            <button onClick={() => { const s = !showGenerator; closeAllPanels(); setShowGenerator(s); }} className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded font-bold shadow-md transition-colors"><IconPlus className="w-4 h-4" /> Create New</button>
          </div>
        </div>

        {/* COMPARISON PANEL */}
        {showCompare && (
          <div className="bg-zinc-800 rounded-lg p-6 mb-6 border border-amber-500/50 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-amber-200">Side-by-Side Comparison</h2>
              <span className="text-xs text-zinc-400 bg-zinc-900 px-3 py-1 rounded-full border border-zinc-700">
                {compareLists.length} / 4 Comparisons Selected
              </span>
            </div>
            <p className="text-sm text-zinc-400 mb-4">
              Select up to 4 lists to view alongside your active <strong>{currentListName}</strong> matrix. 
              Numbers in <span className="text-green-400 font-bold">Green</span> mean you value the player higher than the comparison list.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {availableLists.map(listName => {
                const isActiveMaster = listName === currentListName;
                const isSelected = compareLists.includes(listName);
                return (
                  <button 
                    key={listName} disabled={isActiveMaster} onClick={() => toggleCompareList(listName)} 
                    className={`w-full p-4 rounded border text-left font-semibold transition-colors flex justify-between items-center
                      ${isActiveMaster ? "bg-zinc-900/50 border-zinc-800 text-zinc-600 cursor-not-allowed" 
                      : isSelected ? "bg-amber-900/40 border-amber-500 text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.2)]" 
                      : "bg-zinc-900 border-zinc-700 hover:border-zinc-500 text-zinc-300"}`}
                  >
                    <span className="truncate">{listName}</span>
                    {isActiveMaster && <span className="text-[9px] uppercase tracking-wider bg-zinc-800 px-2 py-0.5 rounded ml-2">Active</span>}
                    {isSelected && <IconCheck className="w-4 h-4 text-amber-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* UPLOADER PANEL */}
        {showUploader && (
          <div className="bg-zinc-800 rounded-lg p-6 mb-6 border border-orange-500/30 shadow-2xl">
            <h2 className="text-xl font-bold text-amber-200 mb-4">Upload Custom Rankings</h2>
            <p className="text-sm text-zinc-400 mb-4">Upload a CSV. We will attempt to automatically map columns for Name, Team, Pos, and Rank/ADP.</p>
            <div className="flex gap-4 items-end mb-4">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Save As (List Name)</label>
                <input type="text" placeholder="e.g. Sleeper ADP June" value={uploadListName} onChange={e => { setUploadListName(e.target.value); setUploadReview(null); }} className="bg-zinc-900 border border-zinc-600 rounded p-3 focus:border-orange-400 outline-none text-sm text-zinc-200"/>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Select CSV File</label>
                <input type="file" accept=".csv" onChange={e => { setUploadFile(e.target.files ? e.target.files[0] : null); setUploadReview(null); }} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm text-zinc-200 w-full"/>
              </div>
              <button onClick={handleUploadSubmit} disabled={isUploading || !uploadReviewReady} className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 px-8 rounded transition-colors disabled:opacity-50">
                {isUploading ? "Validating..." : uploadReview ? "Complete Upload" : "Upload & Parse"}
              </button>
            </div>

            <div className="border-t border-zinc-700 pt-4 mt-2">
              <h3 className="text-sm font-bold text-zinc-300 mb-1">Or Import a Default List</h3>
              <p className="text-xs text-zinc-400 mb-3">Skip the CSV — pull in the latest curated default instead.</p>
              <div className="flex gap-4 items-end">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Save As (List Name)</label>
                  <input type="text" placeholder="e.g. Dynasty Default" value={importDefaultListName} onChange={e => setImportDefaultListName(e.target.value)} className="bg-zinc-900 border border-zinc-600 rounded p-3 focus:border-orange-400 outline-none text-sm text-zinc-200"/>
                </div>
                <button onClick={() => handleImportDefault("DynastySF_Default")} disabled={isImportingDefault || !importDefaultListName} className="bg-zinc-700 hover:bg-zinc-600 text-zinc-200 font-bold py-3 px-5 rounded border border-zinc-600 transition-colors disabled:opacity-50 whitespace-nowrap">
                  Import Dynasty SF
                </button>
                <button onClick={() => handleImportDefault("Redraft_Default")} disabled={isImportingDefault || !importDefaultListName} className="bg-zinc-700 hover:bg-zinc-600 text-zinc-200 font-bold py-3 px-5 rounded border border-zinc-600 transition-colors disabled:opacity-50 whitespace-nowrap">
                  Import Redraft
                </button>
              </div>
            </div>
            {uploadReview && (
              <div className="mt-4 p-4 bg-amber-950/30 border border-amber-500/40 rounded-lg space-y-5">
                <h3 className="text-sm font-bold text-amber-300">Review Player Matches</h3>
                {uploadReview.unrecognized.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-zinc-300 uppercase tracking-wider">Unrecognized Players</div>
                      <div className="flex gap-2">
                        <button onClick={() => setAllUnrecognized("include")} className="text-xs bg-green-900/50 hover:bg-green-800 text-green-200 border border-green-700 rounded px-3 py-1 transition-colors">Include All</button>
                        <button onClick={() => setAllUnrecognized("exclude")} className="text-xs bg-red-950/50 hover:bg-red-900 text-red-200 border border-red-800 rounded px-3 py-1 transition-colors">Exclude All</button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {uploadReview.unrecognized.map(name => (
                        <div key={name} className="flex items-center gap-3 bg-zinc-900/70 border border-zinc-700 rounded p-3 text-sm">
                          <span className="text-zinc-200">{name}</span>
                          <div className="ml-auto flex gap-3">
                            <label className="flex items-center gap-2 text-green-200">
                              <input
                                type="radio"
                                name={`unrecognized-${name}`}
                                checked={unrecognizedDecisions[name] === "include"}
                                onChange={() => setUnrecognizedDecisions(prev => ({ ...prev, [name]: "include" }))}
                                className="accent-green-500"
                              />
                              Include
                            </label>
                            <label className="flex items-center gap-2 text-red-200">
                              <input
                                type="radio"
                                name={`unrecognized-${name}`}
                                checked={unrecognizedDecisions[name] === "exclude"}
                                onChange={() => setUnrecognizedDecisions(prev => ({ ...prev, [name]: "exclude" }))}
                                className="accent-red-500"
                              />
                              Exclude
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadReview.duplicates.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-zinc-300 uppercase tracking-wider">Duplicate Players</div>
                      <button onClick={keepFirstDuplicateRows} className="text-xs bg-green-900/50 hover:bg-green-800 text-green-200 border border-green-700 rounded px-3 py-1 transition-colors">Keep First For All</button>
                    </div>
                    <div className="space-y-3">
                      {uploadReview.duplicates.map(group => {
                        const decision = duplicateDecisions[group.name];
                        return (
                          <div key={group.name} className="bg-zinc-900/70 border border-zinc-700 rounded p-3">
                            <div className="text-sm font-bold text-zinc-100 mb-3">{group.name}</div>
                            <div className="space-y-2">
                              {group.rows.map(row => (
                                <label key={row.row_id} className="flex items-center gap-3 text-sm">
                                  <input
                                    type="radio"
                                    name={`duplicate-${group.name}`}
                                    checked={decision?.action === "keep_one" && decision.row_id === row.row_id}
                                    onChange={() => setDuplicateDecisions(prev => ({ ...prev, [group.name]: { action: "keep_one", row_id: row.row_id } }))}
                                    className="accent-green-500"
                                  />
                                  <span className="text-zinc-200">{row.uploaded_name}</span>
                                  <span className="text-zinc-500 text-xs">Rank {row.rank || "-"} | {row.pos || "-"} | {row.team || "-"}</span>
                                </label>
                              ))}
                              <label className="flex items-center gap-3 text-sm pt-1 border-t border-zinc-700">
                                <input
                                  type="radio"
                                  name={`duplicate-${group.name}`}
                                  checked={decision?.action === "keep_all"}
                                  onChange={() => setDuplicateDecisions(prev => ({ ...prev, [group.name]: { action: "keep_all" } }))}
                                  className="accent-amber-500"
                                />
                                <span className="text-amber-200">These are different players; keep all rows with uploaded names</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {uploadReview.conflicts.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-zinc-300 uppercase tracking-wider">Team / Position Conflicts</div>
                      <div className="flex gap-2">
                        <button onClick={() => setAllConflicts("sleeper")} className="text-xs bg-orange-900/50 hover:bg-orange-800 text-orange-200 border border-orange-700 rounded px-3 py-1 transition-colors">Use Sleeper For All</button>
                        <button onClick={() => setAllConflicts("csv")} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-600 rounded px-3 py-1 transition-colors">Keep CSV For All</button>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-400 mb-3">Your file and Sleeper&apos;s current data disagree on these players. Sleeper is usually more current (trades, position changes).</p>
                    <div className="space-y-2">
                      {uploadReview.conflicts.map(c => (
                        <div key={c.row_id} className="bg-zinc-900/70 border border-zinc-700 rounded p-3 text-sm">
                          <div className="text-zinc-100 font-bold mb-2">{c.name}</div>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-orange-200">
                              <input
                                type="radio"
                                name={`conflict-${c.row_id}`}
                                checked={conflictDecisions[c.row_id] === "sleeper"}
                                onChange={() => setConflictDecisions(prev => ({ ...prev, [c.row_id]: "sleeper" }))}
                                className="accent-orange-500"
                              />
                              Use Sleeper: {c.sleeper_pos || c.csv_pos} · {c.sleeper_team || "FA"}
                            </label>
                            <label className="flex items-center gap-2 text-zinc-300">
                              <input
                                type="radio"
                                name={`conflict-${c.row_id}`}
                                checked={conflictDecisions[c.row_id] === "csv"}
                                onChange={() => setConflictDecisions(prev => ({ ...prev, [c.row_id]: "csv" }))}
                                className="accent-zinc-500"
                              />
                              Keep CSV: {c.csv_pos || c.sleeper_pos} · {c.csv_team || "FA"}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {uploadErrors.length > 0 && (
              <div className="mt-4 p-4 bg-red-900/30 border border-red-800/50 rounded-lg">
                <h3 className="text-sm font-bold text-red-400 mb-2">Error</h3>
                <ul className="list-disc pl-5 text-sm text-red-300 space-y-1">{uploadErrors.map((err, i) => <li key={i}>{err}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        {/* LOADER PANEL */}
        {showLoader && (
          <div className="bg-zinc-800 rounded-lg p-6 mb-6 border border-zinc-600 shadow-2xl">
            <h2 className="text-xl font-bold text-amber-200 mb-4">Saved Rankings</h2>
            {loaderPanelError && (
              <p className="text-sm text-red-400 mb-4">{loaderPanelError}</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {availableLists.map(listName => (
                <div key={listName} className="relative group">
                  <button onClick={() => handleLoadList(listName)} className={`w-full p-4 rounded border text-left font-semibold transition-colors ${listName === currentListName ? "bg-orange-900/40 border-orange-500 text-orange-400" : "bg-zinc-900 border-zinc-700 hover:border-zinc-500 text-zinc-300"}`}>
                    {listName}
                  </button>
                  {availableLists.length > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Are you sure you want to permanently delete '${listName}'?`)) { apiFetch(`/rankings/delete/${encodeURIComponent(listName)}`, { method: 'DELETE' }).then(() => { setAvailableLists(availableLists.filter(l => l !== listName)); if (currentListName === listName) handleLoadList(availableLists.find(l => l !== listName) || "My Rankings"); }); } }} className="absolute top-2 right-2 p-1 bg-red-900/50 hover:bg-red-600 text-red-200 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Delete List"><IconX className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GENERATOR PANEL */}
        {showGenerator && (
          <div className="bg-zinc-800 rounded-lg p-6 mb-6 border border-orange-500/30 shadow-2xl">
            <h2 className="text-xl font-bold text-amber-200 mb-4">Rankings Engine Setup</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">List Name</label>
                <input type="text" value={formConfig.list_name} onChange={e => setFormConfig({...formConfig, list_name: e.target.value})} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm"/>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Base Source</label>
                <select value={formConfig.base_source} onChange={e => setFormConfig({...formConfig, base_source: e.target.value})} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm">
                  <option>My Rankings</option><option>KeepTradeCut Consensus</option><option>FantasyPros Consensus</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">League Size</label>
                <select value={formConfig.teams} onChange={e => setFormConfig({...formConfig, teams: Number(e.target.value)})} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm">
                  <option value={10}>10 Team</option><option value={12}>12 Team</option><option value={14}>14 Team</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Format</label>
                <select value={formConfig.format} onChange={e => setFormConfig({...formConfig, format: e.target.value})} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm">
                  <option>Dynasty</option><option>Redraft</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">QB Scoring</label>
                <select value={formConfig.qb_type} onChange={e => setFormConfig({...formConfig, qb_type: e.target.value})} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm">
                  <option>Superflex</option><option>1QB</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">TE Premium</label>
                <select value={formConfig.te_premium} onChange={e => setFormConfig({...formConfig, te_premium: Number(e.target.value)})} className="bg-zinc-900 border border-zinc-600 rounded p-2 focus:border-orange-400 outline-none text-sm">
                  <option value={0}>None (0.0)</option><option value={0.5}>Low (0.5)</option><option value={1.0}>High (1.0)</option><option value={1.5}>Extreme (1.5)</option>
                </select>
              </div>
            </div>
            <button onClick={handleGenerateRanks} className="w-full bg-zinc-700 hover:bg-zinc-600 text-orange-400 font-bold py-3 rounded border border-zinc-600 transition-colors">Generate Customized Rankings</button>
          </div>
        )}

        {/* H2H PANEL */}
        {showH2H && h2hMatchup && (
          <div className="bg-zinc-800 rounded-lg p-6 mb-6 border border-amber-500/50 shadow-2xl relative">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-amber-200">Head-to-Head Optimizer</h2>
              <div className="flex gap-4 bg-zinc-900/50 p-2 rounded-lg border border-zinc-700">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase text-zinc-400 font-bold tracking-wider">Max Tier Diff</label>
                  <input type="number" value={maxTierDiff} onChange={(e) => setMaxTierDiff(Number(e.target.value))} className="w-16 bg-zinc-800 border border-zinc-600 text-zinc-200 px-2 py-1 rounded text-xs focus:outline-none focus:border-amber-500" min={0}/>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase text-zinc-400 font-bold tracking-wider">Max Rank Diff</label>
                  <input type="number" value={maxRankDiff} onChange={(e) => setMaxRankDiff(Number(e.target.value))} className="w-16 bg-zinc-800 border border-zinc-600 text-zinc-200 px-2 py-1 rounded text-xs focus:outline-none focus:border-amber-500" min={1}/>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center mb-8">
              <div className="text-2xl font-bold text-blue-400 mb-2">
                {h2hMatchup.pA.Name} <span className="text-sm text-zinc-400 font-normal ml-2">Tier {h2hMatchup.pA.Tier} | Rank {h2hMatchup.pA.Rank}</span>
              </div>
              <div className="text-zinc-500 font-black italic my-2">--- VS ---</div>
              <div className="text-2xl font-bold text-red-400 mt-2">
                {h2hMatchup.pB.Name} <span className="text-sm text-zinc-400 font-normal ml-2">Tier {h2hMatchup.pB.Tier} | Rank {h2hMatchup.pB.Rank}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <button onClick={() => handleH2HChoice('1')} className="bg-zinc-700 hover:bg-zinc-600 text-blue-300 py-3 rounded font-bold shadow-sm transition-colors border border-zinc-600">Always {h2hMatchup.pA.Name.split(' ').pop()} <span className="text-xs font-normal text-zinc-400 block">(Tier Above)</span></button>
              <button onClick={() => handleH2HChoice('4')} className="bg-zinc-700 hover:bg-zinc-600 text-red-300 py-3 rounded font-bold shadow-sm transition-colors border border-zinc-600">Always {h2hMatchup.pB.Name.split(' ').pop()} <span className="text-xs font-normal text-zinc-400 block">(Tier Above)</span></button>
              <button onClick={() => handleH2HChoice('2')} className="bg-zinc-700 hover:bg-zinc-600 text-blue-200 py-3 rounded font-bold shadow-sm transition-colors border border-zinc-600">Prefer {h2hMatchup.pA.Name.split(' ').pop()} <span className="text-xs font-normal text-zinc-400 block">(Same Tier)</span></button>
              <button onClick={() => handleH2HChoice('3')} className="bg-zinc-700 hover:bg-zinc-600 text-red-200 py-3 rounded font-bold shadow-sm transition-colors border border-zinc-600">Prefer {h2hMatchup.pB.Name.split(' ').pop()} <span className="text-xs font-normal text-zinc-400 block">(Same Tier)</span></button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6 border-t border-zinc-700 pt-6">
              <button onClick={() => handleH2HChoice('5')} className="bg-zinc-900 hover:bg-zinc-800 text-zinc-400 py-2 rounded text-sm font-semibold border border-zinc-700 transition-colors">Move {h2hMatchup.pA.Name.split(' ').pop()} Down</button>
              <button onClick={() => handleH2HChoice('6')} className="bg-zinc-900 hover:bg-zinc-800 text-zinc-500 py-2 rounded text-sm font-semibold border border-zinc-700 transition-colors">Move BOTH Down</button>
              <button onClick={() => handleH2HChoice('7')} className="bg-zinc-900 hover:bg-zinc-800 text-zinc-400 py-2 rounded text-sm font-semibold border border-zinc-700 transition-colors">Move {h2hMatchup.pB.Name.split(' ').pop()} Down</button>
            </div>

            <div className="flex justify-end items-center pt-2">
              <button onClick={() => handleH2HChoice('skip')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 font-bold text-sm transition-colors"><IconSkipForward className="w-4 h-4" /> Skip Matchup</button>
            </div>
            {lastMoveMsg && (
            <div className="mt-4 p-3 bg-zinc-900 rounded border border-orange-500/30 text-orange-400 text-xs text-center italic">
              {lastMoveMsg}
            </div>
            )}
          </div>
        )}

        {/* MAIN RANKINGS BOARD */}
        {loading ? (
          <p className="animate-pulse text-zinc-400">Loading master rankings...</p>
        ) : (
          <div className="bg-zinc-800 rounded-lg p-6 shadow-xl border border-zinc-700 relative">
            
            {/* UPDATED: STICKY HEADER ZONE */}
            <div className="sticky top-0 z-20 bg-zinc-800 pt-2 pb-2 border-b border-zinc-700 shadow-sm mb-4">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">

                {/* Left Side: Instructions and Filters */}
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="inline-flex items-center gap-1 text-sm text-zinc-400">Drag to reorder. Click <IconBolt className="w-3.5 h-3.5 text-amber-400" /> to split a tier.</p>
                    {editHistory.length > 0 && (
                      <button onClick={handleUndo} className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-400 hover:text-amber-400 transition-colors shrink-0" title="Undo the last change (drag, split, merge, or matchup)">
                        <IconUndo className="w-3.5 h-3.5" /> Undo
                      </button>
                    )}
                  </div>
                  {posFilter !== "All" && (
                    <p className="text-xs text-zinc-500 italic">Split/Merge Tier is disabled while filtered — switch to All to restructure tiers.</p>
                  )}
                  <div className="flex gap-2 bg-zinc-900/50 p-1.5 rounded-lg border border-zinc-700 w-full sm:w-fit overflow-x-auto">
                    {filterPositions.map(pos => (
                      <button key={pos} onClick={() => setPosFilter(pos)} className={`px-3 py-1 text-xs font-bold rounded-md transition-colors shrink-0 ${posFilter === pos ? "bg-orange-600 text-white shadow-sm" : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700"}`}>{pos}</button>
                    ))}
                  </div>
                </div>

                {/* Right Side: Exact Column Alignment for Headers (hidden on mobile — rows show Tier/Rank inline instead) */}
                <div className="hidden sm:flex items-center">
                   <div className="w-[200px] text-right text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-3 pb-1">
                     Current Master
                   </div>

                   {compareLists.map(l => (
                     <div key={l} className="w-[80px] text-center text-[10px] text-zinc-400 font-bold uppercase truncate px-1 pb-1" title={l}>
                       {l.replace(" Rankings", "").substring(0, 8)}
                     </div>
                   ))}

                   {/* Filler for the split button width */}
                   <div className="w-[50px]"></div>
                </div>
              </div>
            </div>

            {players.length === 0 && !loading ? (
              <p className="text-zinc-400 text-center py-10">
                No players in this list yet — use Upload above to import a default list or your own CSV.
              </p>
            ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={displayedPlayers.map((p) => p.Name)} strategy={verticalListSortingStrategy}>
                <ul className="space-y-2">
                  {displayedPlayers.map((player, index) => {
                    const isFirstOfTier = index === 0 || displayedPlayers[index - 1].Tier !== player.Tier;
                    return (
                      <SortablePlayerRow
                        key={player.Name} player={player} showTierHeader={isFirstOfTier}
                        onSplitTier={() => handleSplitTier(player.Name)}
                        onMergeTier={() => handleMergeTierBelow(player.Tier)}
                        compareLists={compareLists}
                        compareData={compareData}
                        tierEditsDisabled={posFilter !== "All"}
                      />
                    );
                  })}
                </ul>
              </SortableContext>
            </DndContext>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
